/**
 * Shared chart helpers (no template-specific logic).
 */

import type { DateRange, MarketSalesTrendItem, MarketShareItem } from '../types';
import { formatMonth } from './apiAndDateHelpers';

export type { DateRange, MarketSalesTrendItem, MarketShareItem };

export const applyDateFilter = (
  data: MarketSalesTrendItem[],
  dateRange: DateRange
): MarketSalesTrendItem[] => {
  if (!dateRange.startDate && !dateRange.endDate) return data;
  const startDate = dateRange.startDate
    ? (() => {
        const d = new Date(dateRange.startDate.getFullYear(), dateRange.startDate.getMonth(), 1);
        d.setHours(0, 0, 0, 0);
        return d;
      })()
    : null;
  const endDate = dateRange.endDate
    ? (() => {
        const d = new Date(dateRange.endDate.getFullYear(), dateRange.endDate.getMonth(), 1);
        d.setHours(0, 0, 0, 0);
        return d;
      })()
    : null;
  const startTime = startDate ? startDate.getTime() : null;
  const endTime = endDate ? endDate.getTime() : null;
  const monthNames: Record<string, number> = {
    jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
    jul: 6, aug: 7, sept: 8, oct: 9, nov: 10, dec: 11,
  };
  return data.filter((item) => {
    const parts = item.month.split('-');
    if (parts.length !== 2) return true;
    const month = monthNames[parts[0].toLowerCase()];
    if (month === undefined) return true;
    const yearShort = parseInt(parts[1], 10);
    if (isNaN(yearShort)) return true;
    const year = 2000 + yearShort;
    const itemDate = new Date(year, month, 1);
    itemDate.setHours(0, 0, 0, 0);
    const itemTime = itemDate.getTime();
    if (startTime !== null && itemTime < startTime) return false;
    if (endTime !== null && itemTime > endTime) return false;
    return true;
  });
};

export const transformMarketShareData = (
  shareData: MarketShareItem[]
): Array<{ vendor: string; share: number; volume: number }> => {
  return shareData.map((item: MarketShareItem) => ({
    vendor: item.labeller || item.vendor || '',
    share:
      typeof item.market_share_by_volume_labeller === 'string'
        ? parseFloat(item.market_share_by_volume_labeller.replace('%', ''))
        : typeof item.share === 'number'
          ? item.share
          : 0,
    volume: item.volume || 0,
  }));
};

export const getMarketTrendsChartData = (
  marketSalesTrendData: MarketSalesTrendItem[],
  filteredMarketSalesTrendData: MarketSalesTrendItem[],
  dateRange: DateRange
) => {
  const hasDateFilter = !!(dateRange.startDate || dateRange.endDate);
  let dataToUse: MarketSalesTrendItem[];
  if (hasDateFilter) {
    if (filteredMarketSalesTrendData.length > 0) {
      dataToUse = filteredMarketSalesTrendData;
    } else if (marketSalesTrendData.length > 0) {
      dataToUse = applyDateFilter(marketSalesTrendData, dateRange);
    } else {
      dataToUse = [];
    }
  } else {
    dataToUse = marketSalesTrendData;
  }
  if (!dataToUse || dataToUse.length === 0) {
    return { labels: [], datasets: [] };
  }
  return {
    labels: dataToUse.map(d => formatMonth(d.month)),
    datasets: [
      {
        type: 'bar' as const,
        label: 'DOD Volume',
        data: dataToUse.map(d => d.dod_volume || 0),
        backgroundColor: '#5b5fd6',
        borderColor: '#5b5fd6',
        borderWidth: 1,
        yAxisID: 'y',
        order: 2,
        stack: 'volume',
      },
      {
        type: 'bar' as const,
        label: 'VA Volume',
        data: dataToUse.map(d => d.va_volume || 0),
        backgroundColor: '#00bcd4',
        borderColor: '#00bcd4',
        borderWidth: 1,
        yAxisID: 'y',
        order: 2,
        stack: 'volume',
      },
      {
        type: 'line' as const,
        label: 'Sales',
        data: dataToUse.map(d => d.sales || 0),
        borderColor: '#f59e0b',
        backgroundColor: '#f59e0b',
        borderWidth: 2,
        fill: false,
        tension: 0,
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        yAxisID: 'y1',
        order: 1,
      },
    ],
  };
};
